<script>
  import Icon from "@iconify/svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";
  import { push } from "svelte-spa-router";
  import { addToast } from "../components/Toast.svelte";
  import Layout from "../components/Layout.svelte";
  import Card from "../components/Card.svelte";
  import Skeleton from "../components/Skeleton.svelte";
  import Button from "../components/Button.svelte";
  import Input from "../components/Input.svelte";
  import StatusBadge from "../components/StatusBadge.svelte";
  import EmptyState from "../components/EmptyState.svelte";

  let { params = {} } = $props();

  let loading = $state(true);
  let error = $state("");
  let viewer = $state(null);
  let saving = $state(false);

  // Edit form
  let editLabel = $state("");
  let editAllSites = $state(false);

  // Tabs
  let activeTab = $state("rules");
  const tabs = [
    { key: "rules", label: () => t("rules.title"), icon: "mdi:shield-lock" },
    { key: "invites", label: () => t("invites.title"), icon: "mdi:link-variant" },
    { key: "sessions", label: () => t("sessions.title"), icon: "mdi:lan-connect" },
  ];

  // Rules
  let rules = $state([]);
  let rulesLoading = $state(false);
  let newRuleValue = $state("");
  let addingRule = $state(false);

  // Invites
  let invites = $state([]);
  let invitesLoading = $state(false);
  let generatingInvite = $state(false);
  let copiedId = $state(null);

  // Sessions
  let sessions = $state([]);
  let sessionsLoading = $state(false);
  let revokingAll = $state(false);
  let revokingId = $state(null);

  let viewerId = $derived(params.id);

  $effect(() => {
    if (viewerId) {
      loadViewer();
      loadRules();
      loadInvites();
      loadSessions();
    }
  });

  async function loadViewer() {
    loading = true;
    error = "";
    try {
      viewer = await api.get(`/viewers/${viewerId}`);
      editLabel = viewer.label || "";
      editAllSites = viewer.all_sites || false;
    } catch (err) {
      error = err.message || t("common.error");
    } finally {
      loading = false;
    }
  }

  async function handleSave() {
    saving = true;
    try {
      await api.put(`/viewers/${viewerId}`, {
        label: editLabel,
        all_sites: editAllSites,
      });
      addToast(t("common.success"), "success");
      await loadViewer();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      saving = false;
    }
  }

  // --- Rules ---
  async function loadRules() {
    rulesLoading = true;
    try {
      const data = await api.get(`/viewers/${viewerId}/rules`);
      rules = data?.rules ?? data ?? [];
    } catch {
      rules = [];
    } finally {
      rulesLoading = false;
    }
  }

  async function addRule() {
    if (!newRuleValue.trim()) return;
    addingRule = true;
    try {
      await api.post(`/viewers/${viewerId}/rules`, { value: newRuleValue, action: "deny" });
      addToast(t("common.success"), "success");
      newRuleValue = "";
      await loadRules();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      addingRule = false;
    }
  }

  async function removeRule(ruleId) {
    try {
      await api.del(`/viewers/${viewerId}/rules/${ruleId}`);
      addToast(t("common.success"), "success");
      await loadRules();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    }
  }

  // --- Invites ---
  async function loadInvites() {
    invitesLoading = true;
    try {
      const data = await api.get(`/viewers/${viewerId}/invites`);
      invites = data?.invites ?? data ?? [];
    } catch {
      invites = [];
    } finally {
      invitesLoading = false;
    }
  }

  async function generateInvite() {
    generatingInvite = true;
    try {
      await api.post(`/viewers/${viewerId}/invites`);
      addToast(t("common.success"), "success");
      await loadInvites();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      generatingInvite = false;
    }
  }

  async function copyInviteLink(invite) {
    const link = invite.link || invite.url || "";
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      copiedId = invite.id;
      setTimeout(() => (copiedId = null), 2000);
    } catch {
      addToast(t("common.error"), "error");
    }
  }

  // --- Sessions ---
  async function loadSessions() {
    sessionsLoading = true;
    try {
      const data = await api.get(`/viewers/${viewerId}/sessions`);
      sessions = data?.sessions ?? data ?? [];
    } catch {
      sessions = [];
    } finally {
      sessionsLoading = false;
    }
  }

  async function revokeSession(sessionId) {
    revokingId = sessionId;
    try {
      await api.del(`/viewers/${viewerId}/sessions/${sessionId}`);
      addToast(t("common.success"), "success");
      await loadSessions();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      revokingId = null;
    }
  }

  async function revokeAllSessions() {
    revokingAll = true;
    try {
      await api.del(`/viewers/${viewerId}/sessions`);
      addToast(t("common.success"), "success");
      await loadSessions();
    } catch (err) {
      addToast(err.message || t("common.error"), "error");
    } finally {
      revokingAll = false;
    }
  }

  function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Back link -->
    <button
      type="button"
      class="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
      onclick={() => push("/viewers")}
    >
      <Icon icon="mdi:arrow-left" class="w-4 h-4" />
      {t("common.back")}
    </button>

    {#if error}
      <div class="px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
        {error}
      </div>
    {/if}

    {#if loading}
      <Card><Skeleton lines={6} /></Card>
    {:else if viewer}
      <!-- Viewer info & edit -->
      <Card>
        <div class="space-y-4">
          <div class="flex items-center gap-3">
            <h1 class="text-xl font-semibold text-white">{viewer.label || "-"}</h1>
            <StatusBadge status={viewer.status || "active"} />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label={t("viewers.label")} bind:value={editLabel} />
            <div class="flex items-end">
              <label class="flex items-center gap-2 cursor-pointer h-10">
                <input
                  type="checkbox"
                  bind:checked={editAllSites}
                  class="w-4 h-4 rounded border-white/20 bg-black/30 text-lime-500 focus:ring-lime-500/30"
                />
                <span class="text-sm text-zinc-300">{t("viewers.all_sites")}</span>
              </label>
            </div>
          </div>

          <div class="flex justify-end">
            <Button onclick={handleSave} loading={saving}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Card>

      <!-- Tabs -->
      <div class="flex gap-1 border-b border-white/10 pb-0">
        {#each tabs as tab}
          <button
            type="button"
            class="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px {activeTab === tab.key ? 'border-lime-500 text-white' : 'border-transparent text-zinc-400 hover:text-white'}"
            onclick={() => (activeTab = tab.key)}
          >
            <Icon icon={tab.icon} class="w-4 h-4" />
            {tab.label()}
          </button>
        {/each}
      </div>

      <!-- Rules tab -->
      {#if activeTab === "rules"}
        <Card>
          <div class="space-y-4">
            <div class="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                bind:value={newRuleValue}
                placeholder={t("rules.deny")}
                class="flex-1 h-10 px-4 bg-black/30 border border-white/10 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-400/20 focus:border-zinc-400/40 text-sm"
                onkeydown={(e) => { if (e.key === "Enter") addRule(); }}
              />
              <Button onclick={addRule} loading={addingRule} disabled={addingRule || !newRuleValue.trim()}>
                <Icon icon="mdi:plus" class="w-4 h-4" />
                {t("rules.add")}
              </Button>
            </div>

            {#if rulesLoading}
              <Skeleton lines={3} />
            {:else if rules.length === 0}
              <p class="text-sm text-zinc-500 py-4 text-center">{t("rules.no_rules")}</p>
            {:else}
              <div class="space-y-2">
                {#each rules as rule (rule.id)}
                  <div class="flex items-center justify-between gap-3 px-3 py-2 rounded border border-white/5 bg-white/[0.02]">
                    <div class="flex items-center gap-2 min-w-0">
                      <StatusBadge status={rule.action || "deny"} />
                      <span class="text-sm text-white truncate">{rule.value || "-"}</span>
                    </div>
                    <button
                      type="button"
                      class="text-zinc-500 hover:text-rose-400 transition-colors flex-shrink-0"
                      onclick={() => removeRule(rule.id)}
                      title={t("common.delete")}
                    >
                      <Icon icon="mdi:close" class="w-4 h-4" />
                    </button>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </Card>
      {/if}

      <!-- Invites tab -->
      {#if activeTab === "invites"}
        <Card>
          <div class="space-y-4">
            <div class="flex justify-end">
              <Button onclick={generateInvite} loading={generatingInvite}>
                <Icon icon="mdi:link-plus" class="w-4 h-4" />
                {t("invites.generate")}
              </Button>
            </div>

            {#if invitesLoading}
              <Skeleton lines={3} />
            {:else if invites.length === 0}
              <p class="text-sm text-zinc-500 py-4 text-center">{t("invites.no_invites")}</p>
            {:else}
              <div class="space-y-2">
                {#each invites as invite (invite.id)}
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-3 rounded border border-white/5 bg-white/[0.02]">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm text-white font-mono truncate">{invite.link || invite.url || invite.token || "-"}</p>
                      {#if invite.expires_at}
                        <p class="text-xs text-zinc-500 mt-0.5">{t("invites.expires")}: {formatDate(invite.expires_at)}</p>
                      {/if}
                      {#if invite.status}
                        <div class="mt-1">
                          <StatusBadge status={invite.status} />
                        </div>
                      {/if}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onclick={() => copyInviteLink(invite)}
                    >
                      <Icon icon={copiedId === invite.id ? "mdi:check" : "mdi:content-copy"} class="w-3.5 h-3.5" />
                      {copiedId === invite.id ? t("invites.copied") : t("invites.copy")}
                    </Button>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </Card>
      {/if}

      <!-- Sessions tab -->
      {#if activeTab === "sessions"}
        <Card>
          <div class="space-y-4">
            {#if sessions.length > 0}
              <div class="flex justify-end">
                <Button variant="danger" onclick={revokeAllSessions} loading={revokingAll}>
                  <Icon icon="mdi:close-circle-multiple" class="w-4 h-4" />
                  {t("sessions.revoke_all")}
                </Button>
              </div>
            {/if}

            {#if sessionsLoading}
              <Skeleton lines={4} />
            {:else if sessions.length === 0}
              <p class="text-sm text-zinc-500 py-4 text-center">{t("sessions.no_sessions")}</p>
            {:else}
              <div class="space-y-2">
                {#each sessions as session (session.id)}
                  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-3 rounded border border-white/5 bg-white/[0.02]">
                    <div class="min-w-0">
                      <p class="text-sm text-white">{session.ip || session.user_agent || session.id}</p>
                      {#if session.last_seen_at}
                        <p class="text-xs text-zinc-500 mt-0.5">{t("sessions.last_seen")}: {formatDate(session.last_seen_at)}</p>
                      {/if}
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onclick={() => revokeSession(session.id)}
                      loading={revokingId === session.id}
                    >
                      {t("sessions.revoke")}
                    </Button>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        </Card>
      {/if}
    {/if}
  </div>
</Layout>
